export interface ActiveDetail {
  name: string;
  percent: number;
  order?: number;
  vrn?: number;
  vrnUnit?: string;
  vrnChildren?: number;
  vrnChildrenUnit?: string;
  ul?: number;
  ulUnit?: string;
  iuClaim?: number;
  limitSolids?: number;
  limitSolidsUnit?: string;
  limitLiquids?: number;
  limitLiquidsUnit?: string;
  limitSyrups?: number;
  limitSyrupsUnit?: string;
  recDairyIntake?: number;
  recDairyIntakeUnit?: string;
}

export interface ActiveBreakdown extends ActiveDetail {
  mgPlus?: number;
}

export interface ActiveMetaFields {
  vrn?: number;
  vrnUnit?: string;
  vrnChildren?: number;
  vrnChildrenUnit?: string;
  ul?: number;
  ulUnit?: string;
  iuClaim?: number;
  limitSolids?: number;
  limitSolidsUnit?: string;
  limitLiquids?: number;
  limitLiquidsUnit?: string;
  limitSyrups?: number;
  limitSyrupsUnit?: string;
  recDairyIntake?: number;
  recDairyIntakeUnit?: string;
}

const getActiveName = (active: any): string => {
  return active?.active?.name ?? active?.activeName?.name ?? active?.name ?? active?.activeName ?? '';
};

const getActiveEntity = (active: any): any => {
  return active?.active ?? active?.activeName ?? active;
};

const getActiveOrder = (active: any): number | undefined => {
  return toOptionalNumber(active?.order);
};

const sortActiveProductsByOrder = (activeProducts: any[]): any[] => {
  return [...activeProducts].sort((a, b) => {
    const aOrder = getActiveOrder(a);
    const bOrder = getActiveOrder(b);

    if (aOrder != null && bOrder != null) {
      return aOrder - bOrder;
    }
    if (aOrder != null) {
      return -1;
    }
    if (bOrder != null) {
      return 1;
    }
    return 0;
  });
};

const toOptionalNumber = (value: any): number | undefined => {
  if (value == null || value === '') {
    return undefined;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const extractActiveMeta = (activeEntity: any): ActiveMetaFields => {
  if (!activeEntity || typeof activeEntity !== 'object') {
    return {};
  }

  return {
    vrn: toOptionalNumber(activeEntity.vrn),
    vrnUnit: activeEntity.vrnUnit ?? activeEntity.vrn_unit,
    vrnChildren: toOptionalNumber(activeEntity.vrnChildren),
    vrnChildrenUnit: activeEntity.vrnChildrenUnit,
    ul: toOptionalNumber(activeEntity.ul),
    ulUnit: activeEntity.ulUnit,
    iuClaim: toOptionalNumber(activeEntity.iuClaim),
    limitSolids: toOptionalNumber(activeEntity.limitSolids),
    limitSolidsUnit: activeEntity.limitSolidsUnit,
    limitLiquids: toOptionalNumber(activeEntity.limitLiquids),
    limitLiquidsUnit: activeEntity.limitLiquidsUnit,
    limitSyrups: toOptionalNumber(activeEntity.limitSyrups),
    limitSyrupsUnit: activeEntity.limitSyrupsUnit,
    recDairyIntake: toOptionalNumber(activeEntity.recDairyIntake),
    recDairyIntakeUnit: activeEntity.recDairyIntakeUnit,
  };
};

export const extractActiveDetails = (item: any): ActiveDetail[] => {
  const activeProducts = sortActiveProductsByOrder(item?.activeProducts || item?.actives || []);
  const actives: ActiveDetail[] = activeProducts
    .map((active: any) => {
      const activeEntity = getActiveEntity(active);
      return {
        name: getActiveName(active),
        percent: Number((active as any)?.activeValue) || 0,
        order: getActiveOrder(active),
        ...extractActiveMeta(activeEntity),
      };
    })
    .filter((active: ActiveDetail) => active.name || active.percent);

  const mainActiveName = item?.mainActive?.name ?? item?.mainActiveName ?? '';
  const mainActiveValue = Number(item?.mainActiveValue) || Number(activeProducts?.[0]?.activeValue) || 0;
  const mainActiveMeta = extractActiveMeta(
    typeof item?.mainActive === 'object' ? item.mainActive : getActiveEntity(activeProducts?.[0]),
  );

  if (!actives.length && (mainActiveName || mainActiveValue)) {
    return [{name: mainActiveName, percent: mainActiveValue, ...mainActiveMeta}];
  }

  if (actives.length) {
    if (mainActiveName && !actives[0].name) {
      actives[0].name = mainActiveName;
    }
    if (mainActiveValue && !actives[0].percent) {
      actives[0].percent = mainActiveValue;
    }
    actives[0] = {
      ...mainActiveMeta,
      ...actives[0],
    };
  }

  return actives;
};

const pickNumber = (...values: any[]): number | undefined => {
  for (const value of values) {
    if (value == null || value === '') {
      continue;
    }
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
  }
  return undefined;
};

export const resolveMainActiveFields = (item: any, mainActive?: ActiveDetail): ActiveMetaFields => {
  const mainActiveEntity = typeof item?.mainActive === 'object' ? item.mainActive : undefined;

  return {
    vrn: pickNumber(mainActive?.vrn, mainActiveEntity?.vrn, item?.vrn),
    vrnUnit: mainActive?.vrnUnit ?? mainActiveEntity?.vrnUnit ?? item?.vrnUnit,
    vrnChildren: pickNumber(mainActive?.vrnChildren, mainActiveEntity?.vrnChildren, item?.vrnChildren),
    vrnChildrenUnit: mainActive?.vrnChildrenUnit ?? mainActiveEntity?.vrnChildrenUnit ?? item?.vrnChildrenUnit,
    ul: pickNumber(mainActive?.ul, mainActiveEntity?.ul, item?.ul),
    ulUnit: mainActive?.ulUnit ?? mainActiveEntity?.ulUnit ?? item?.ulUnit,
    iuClaim: pickNumber(mainActive?.iuClaim, mainActiveEntity?.iuClaim, item?.iuClaim),
    limitSolids: pickNumber(mainActive?.limitSolids, mainActiveEntity?.limitSolids, item?.limitSolids),
    limitLiquids: pickNumber(mainActive?.limitLiquids, mainActiveEntity?.limitLiquids, item?.limitLiquids),
    limitSyrups: pickNumber(
      mainActive?.limitSyrups,
      mainActiveEntity?.limitSyrups,
      item?.limitSyrups,
      item?.limitSyrupGels,
    ),
    recDairyIntake: pickNumber(
      mainActive?.recDairyIntake,
      mainActiveEntity?.recDairyIntake,
      item?.recDairyIntake,
      item?.rdi,
    ),
    limitSolidsUnit:
      mainActive?.limitSolidsUnit ?? mainActiveEntity?.limitSolidsUnit ?? item?.limitSolidsUnit,
    limitLiquidsUnit:
      mainActive?.limitLiquidsUnit ?? mainActiveEntity?.limitLiquidsUnit ?? item?.limitLiquidsUnit,
    limitSyrupsUnit:
      mainActive?.limitSyrupsUnit ?? mainActiveEntity?.limitSyrupsUnit ?? item?.limitSyrupsUnit,
    recDairyIntakeUnit:
      mainActive?.recDairyIntakeUnit ?? mainActiveEntity?.recDairyIntakeUnit ?? item?.recDairyIntakeUnit,
  };
};

export const getMaxActiveCount = (items: any[]): number => {
  if (!items || items.length === 0) {
    return 1;
  }

  return items.reduce((max, item) => {
    const actives = item?.activeDetails ?? extractActiveDetails(item);
    return Math.max(max, actives.length || 1);
  }, 1);
};
